import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { catchError, filter, forkJoin, map, mergeMap, of, switchMap, takeUntil, tap } from 'rxjs';
import { LocalStorageService } from '../../../core/services/local-storage/local-storage.service';
import { clearAllState, startTourIfUnseen } from '../../../core/store/actions/app.actions';
import { patientSwitchInitiated } from '../../../user/store/actions/user.actions';
import { TourType } from '../../../tours/data/tour-config';
import { selectPatientId } from '../../../user/store/selectors/user.selectors';
import { Medication } from '../../models/medication';
import { MedicationRefill } from '../../models/medication-refill';
import { RefillStatus } from '../../models/refill-status-enum';
import { SelectedMedication } from '../../models/selected-medication';
import { MedicationPrescriptionsService } from '../../services/medication-prescriptions/medication-prescriptions.service';
import { PatientMedicationRefillsService } from '../../services/medication-refills/medication-refills.service';
import { PatientMedicationsService } from '../../services/medications/medications.service';
import * as actions from '../actions/medications.actions';
import { selectPatientMedicationCount, selectSelectedMedications } from '../selectors/medications.selector';

@Injectable()
export class MedicationEffects {
  private actions$ = inject(Actions);
  private patientMedicationsService = inject(PatientMedicationsService);
  private patientMedicationRefillsService = inject(PatientMedicationRefillsService);
  private localStorageService = inject(LocalStorageService);
  private medicationPrescriptionsService = inject(MedicationPrescriptionsService);
  private store = inject(Store);
  private router = inject(Router);
  private readonly cancelRequest$ = this.actions$.pipe(ofType(patientSwitchInitiated, clearAllState));

  private mapMedicationRefillStatus(
    medication: Medication,
    refill: MedicationRefill | undefined
  ): Medication['patientMedicationRefillStatusType'] {
    if (medication.patientMedicationRefillStatusType === RefillStatus.Removed) {
      return RefillStatus.Removed;
    }

    if (medication.patientMedicationRefillStatusType === RefillStatus.Stopped) {
      return RefillStatus.Stopped;
    }

    if (refill?.patientMedicationRefillStatusDescription?.toLowerCase() === 'pending') {
      return RefillStatus.Pending;
    }

    if (medication.isRefillAllowed) {
      return RefillStatus.RequestRefill;
    }

    return medication.patientMedicationRefillStatusType ?? null;
  }

  private mergeMedicationStatuses(medications: Medication[] | null, refills: MedicationRefill[] | null): Medication[] {
    const refillByMedicationId = new Map((refills ?? []).map(refill => [refill.patientMedicationId, refill]));

    return [...(medications ?? [])]
      .map(medication => ({
        ...medication,
        patientMedicationRefillStatusType: this.mapMedicationRefillStatus(
          medication,
          refillByMedicationId.get(medication.patientMedicationId)
        ),
      }))
      .sort((a, b) =>
        (a.medicationName ?? '').localeCompare(b.medicationName ?? '', undefined, {
          numeric: true,
          sensitivity: 'base',
        })
      );
  }

  getMedications$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(actions.getPatientMedications),
      concatLatestFrom(() => [this.store.select(selectPatientId)]),
      mergeMap(([, patientId]) => {
        return forkJoin({
          medications: this.patientMedicationsService.getPatientMedications(patientId),
          refills: this.patientMedicationRefillsService.getPatientMedicationRefills(patientId),
        }).pipe(
          takeUntil(this.cancelRequest$),
          map(({ medications, refills }) =>
            actions.getPatientMedicationsSuccess({
              medications: this.mergeMedicationStatuses(medications, refills),
            })
          ),
          catchError(error => {
            // Pass the HttpErrorResponse itself, not `error.error`. The unwrapped body is null for an
            // empty-body 5xx, which made the stored error falsy — so MedicationsComponent skipped its
            // error branch and rendered "No current medications on file", a false clinical negative.
            // The full response is also what getLoadErrorCopy needs to tell timeout from offline.
            return of(actions.getPatientMedicationsFailure({ error }));
          })
        );
      })
    );
  });

  createRefillRequest$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(actions.createRefillRequest),
      concatLatestFrom(() => [this.store.select(selectPatientId), this.store.select(selectSelectedMedications)]),
      mergeMap(([action, patientId, selectedMeds]) => {
        const requests =
          selectedMeds == null
            ? []
            : selectedMeds.map(selectedMed =>
                this.patientMedicationRefillsService.createRefillRequest(
                  selectedMed.medicationId,
                  patientId,
                  action.pharmacyId
                )
              );

        return forkJoin(requests).pipe(
          takeUntil(this.cancelRequest$),
          map(() => actions.createRefillRequestSuccess()),
          catchError(err => {
            return of(actions.createRefillRequestFailure({ error: err.error?.errors || err }));
          })
        );
      })
    );
  });

  navigateToRefillConfirmation$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(actions.createRefillRequestSuccess),
        concatLatestFrom(() => [this.store.select(selectPatientId)]),
        tap(([, patientId]) => {
          const route = ['patient', patientId, 'medication', 'confirmation', 'medication'];
          this.router.navigate(route);
        })
      );
    },
    { dispatch: false }
  );

  navigateToMedicationVerification$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(actions.navigateToMedicationVerification),
        concatLatestFrom(() => [this.store.select(selectPatientId), this.store.select(selectSelectedMedications)]),
        tap(([, patientId, selectedMeds]) => {
          const route = ['patient', patientId, 'medication', 'verification'];
          const queryParams = { medicationIds: selectedMeds };
          this.router.navigate(route, { queryParams });
        })
      );
    },
    { dispatch: false }
  );

  getMedicationsByIds$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(actions.getPatientMedicationById),
      concatLatestFrom(() => [this.store.select(selectPatientId)]),
      mergeMap(([, patientId]) => {
        const selectedMeds: SelectedMedication[] = this.localStorageService.get('selectedMeds') || [];
        return of(selectedMeds).pipe(
          switchMap(meds => {
            const requests =
              meds == null
                ? []
                : meds.map(med => this.patientMedicationsService.getPatientMedicationById(med.medicationId, patientId));
            return forkJoin(requests).pipe(
              takeUntil(this.cancelRequest$),
              map(medicationsArray => {
                const allMeds = medicationsArray.flat();
                return actions.getPatientMedicationByIdSuccess({ medications: allMeds, selectedMeds: meds });
              }),
              catchError(err => {
                return of(actions.getPatientMedicationByIdFailure({ error: err.error?.errors || err }));
              })
            );
          }),
          catchError(error => {
            return of(actions.getPatientMedicationByIdFailure({ error: error.error }));
          })
        );
      })
    );
  });

  getSelectedMedications$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(actions.getSelectedMedications),
      map(() => {
        const selectedMeds: SelectedMedication[] = this.localStorageService.get('selectedMeds') || [];
        return actions.getSelectedMedicationsSuccess({ medications: selectedMeds });
      })
    );
  });

  setSelectedMedications$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(actions.setSelectedMedications),
        tap(action => this.localStorageService.set('selectedMeds', action.medications))
      );
    },
    { dispatch: false }
  );

  removeSelectedMedications$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(actions.clearMedicationsState, actions.createRefillRequestSuccess),
      map(() => {
        this.localStorageService.remove('selectedMeds');
        return actions.clearMedicationsStateSuccess();
      })
    );
  });

  // Purge the remembered medication selection on a patient switch and on clearAllState (logout /
  // account switch), so it never outlives the session that owns it — matching cancelRequest$, which
  // cancels in-flight requests on both actions.
  clearSelectedMedsOnPatientSwitch$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(patientSwitchInitiated, clearAllState),
        tap(() => this.localStorageService.remove('selectedMeds'))
      );
    },
    { dispatch: false }
  );

  getMedicationPrescriptions$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(actions.getMedicationPrescriptions),
      concatLatestFrom(() => [this.store.select(selectPatientId)]),
      mergeMap(([action, patientId]) =>
        this.medicationPrescriptionsService.getPatientMedicationPrescriptions(action.medicationId, patientId).pipe(
          takeUntil(this.cancelRequest$),
          map(prescriptions =>
            actions.getMedicationPrescriptionsSuccess({
              medicationId: action.medicationId,
              isHomeMedication: action.isHomeMedication,
              prescriptions: [...prescriptions].sort(
                (a, b) => b.patientMedicationPrescriptionId - a.patientMedicationPrescriptionId
              ),
            })
          ),
          catchError(error => of(actions.getMedicationPrescriptionsFailure({ error })))
        )
      )
    );
  });

  startMedicationsTourIfUnseen$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(actions.getPatientMedicationsSuccess),
      concatLatestFrom(() => this.store.select(selectPatientMedicationCount)),
      //only start medications tour if there are medications to show
      filter(([, count]) => count > 0),
      map(() => {
        return startTourIfUnseen({ tourType: TourType.Medications });
      })
    );
  });
}
